<script setup>
import { computed, useImageSource } from '#imports';
import { hexToRgb } from '~/helpers/hex-to-rgb';

const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
  isNextArticle: {
    type: Boolean,
    default: false,
  },
  isArticleCardsType: {
    type: Boolean,
    default: false,
  },
  isArticleOpinionType: {
    type: Boolean,
    default: false,
  },
});

const title = computed(() => props.article.title);
const subtitle = computed(() => props.article.subtitle);
const classes = computed(() => ({
  'article-header-card--next-article': props.isNextArticle,
  'article-header-card--cards-type': props.isArticleCardsType,
  'article-header-card--opinion-type': props.isArticleOpinionType,
}));
const caption = computed(
  () => props.article?.image?.caption || `Изображение-${title.value}`
);
const getColor = (colorFromPicker, defaultColor) =>
  colorFromPicker?.hex || defaultColor;

const shadowColorCustom = computed(() =>
  getColor(props.article.shadowColorFromColorPicker, props.article.shadowColor)
);

const shadowColorCardsCustom = computed(() =>
  getColor(
    props.article.headerShadowColorFromColorPicker,
    props.article.shadowColor
  )
);

const markColorCustom = computed(() =>
  getColor(
    props.article.titleMarkColorFromColorPicker,
    props.article.titleMarkColor
  )
);

const markColor = computed(() => {
  const color = markColorCustom.value
    ? hexToRgb(markColorCustom.value)
    : '251, 255, 53';
  return { '--mark-color': color };
});

const shadowColor = computed(() => {
  const color = shadowColorCustom
    ? hexToRgb(shadowColorCustom.value)
    : '24, 28, 53';

  const colorCards = shadowColorCardsCustom
    ? hexToRgb(shadowColorCardsCustom.value)
    : '24, 28, 53';
  return { '--shadow-color': props.isArticleCardsType ? colorCards : color };
});
</script>

<template>
  <div class="article-header-card" :class="classes" :style="markColor">
    <picture>
      <source
        media="(min-width: 768px)"
        :srcset="`
        ${useImageSource({
          image: article.image,
          height: 900,
        })} 1x, ${useImageSource({
          image: article.image,
          height: 900,
          dpr: 2,
        })} 2x`"
      />
      <NuxtImg
        class="article-header-card__image"
        :alt="caption"
        :src="
          useImageSource({
            image: article.image,
            width: 500,
            height: 900,
            fit: 'crop',
            crop: 'center',
            dpr: 1,
          })
        "
        :srcset="`${useImageSource({
          image: article.image,
          width: 500,
          height: 900,
          fit: 'crop',
          crop: 'center',
          dpr: 2,
        })} 2x`"
      />
    </picture>
    <div class="article-header-card__container">
      <h1 class="article-header-card__title">
        <span>{{ title }}</span>
      </h1>
      <p class="article-header-card__subtitle">
        <span>{{ subtitle }}</span>
      </p>
    </div>
    <div
      v-if="isArticleCardsType || isArticleOpinionType"
      class="article-header-card__shadow"
      :style="shadowColor"
    ></div>
  </div>
</template>

<style lang="postcss" scoped>
.article-header-card {
  --mark-color: 251, 255, 53;
  --shadow-color: 24, 28, 53;

  position: relative;
  height: 810px;
  border-radius: 0 0 24px 24px;
  overflow: hidden;

  &__image {
    position: absolute;
    z-index: 1;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__container {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    max-width: var(--content-max-width);
    height: 100%;
    margin: 0 auto;
    padding: 0 60px 30px;
  }

  &__title {
    max-width: 1075px;
    margin-bottom: 8px;
    font-family: var(--title-font-family);
    font-size: var(--h1-font-size);
    line-height: 50px;
  }

  &__subtitle {
    max-width: 736px;
    margin-bottom: calc(var(--main-header-height) + 24px);
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-family: var(--body-font-family);
  }

  &__title span,
  &__subtitle span {
    background-color: rgba(var(--mark-color), 90%);
    box-shadow: 10px 0 0 rgba(var(--mark-color), 90%),
      -10px 0 0 rgba(var(--mark-color), 90%);
    box-decoration-break: clone;
  }

  &__authors {
    display: flex;
    justify-content: flex-end;
  }

  &__shadow {
    position: absolute;
    z-index: 1;
    left: 0;
    right: 0;
    bottom: 0;
    height: 300px;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(var(--shadow-color)) 100%
    );
  }

  &--next-article {
    height: 588px;

    .article-header-card__subtitle {
      margin-bottom: 50px;
    }

    .article-header-card__image {
      object-position: top;
    }
  }

  &--cards-type {
    .article-header-card__title,
    .article-header-card__subtitle {
      color: var(--white-color);
    }

    .article-header-card__title span,
    .article-header-card__subtitle span {
      background-color: rgba(var(--mark-color), 40%);
      box-shadow: 10px 0 0 rgba(var(--mark-color), 40%),
        -10px 0 0 rgba(var(--mark-color), 40%);
      box-decoration-break: clone;
    }
  }

  &--opinion-type {
    border: 2px solid rgba(var(--mark-color));
    border-radius: 24px;

    .article-header-card__shadow {
      height: 81px;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0%) 0%,
        rgba(0, 0, 0, 80%) 100%
      );
    }

    .article-header-card__container {
      justify-content: flex-start;
      padding: 48px 60px 30px;
    }
  }

  @media (--large-vp) {
    height: 100vh;

    &--next-article {
      height: 588px;
    }

    &--opinion-type {
      height: calc(100vh - 178px);
    }
  }

  @media (--small-vp) {
    border-radius: 0 0 16px 16px;
    height: var(--cover-heigth-mobile);

    &__container {
      padding: 0 12px 12px;
    }

    &__title {
      margin-bottom: 8px;
      font-size: 24px;
      line-height: 30px;
    }

    &__subtitle {
      font-size: 16px;
      line-height: 19.5px;
      font-family: var(--body-font-family);
    }

    &__title span,
    &__subtitle span {
      box-shadow: 4px 0 0 rgba(var(--mark-color), 90%),
        -4px 0 0 rgba(var(--mark-color), 90%);
    }

    &--next-article {
      height: 400px;
    }

    &--opinion-type {
      border-radius: 16px;
      height: calc(var(--cover-heigth-mobile) - 90px);

      .article-header-card__container {
        justify-content: flex-end;
        padding: 0 12px 12px;
      }
    }
  }
}
</style>
